import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React from 'react';
import { TriangleAlert } from 'lucide-react';
import { DateTime, Vehicle } from 'components/Erp/Display';
import styles from '../ReportCard.module.scss';
import { checkBranchesInRange } from '../utils/geo';

/**
 * Панель информации о водителе для оператора (TopBase / CHPP / PrivatePoint).
 * Единый компонент вместо трёх копий.
 *
 * @param {Object} props
 * @param {string}   props.operatorType     — 'topbase' | 'chpp' | 'privatepoint'
 * @param {Object}   props.scannedDriver    — данные водителя
 * @param {Object}   props.lastLoadA        — последняя загрузка
 * @param {boolean}  props.scanLoading
 * @param {string}   props.scanError
 * @param {boolean}  props.confirmLoading
 * @param {string}   props.confirmError
 * @param {string}   props.userGeoLocation
 * @param {Array}    props.operatorBranches
 * @param {Function} props.onConfirm
 */
const OperatorDriverPanel = ({
  operatorType,
  scannedDriver,
  lastLoadA,
  scanLoading,
  scanError,
  confirmLoading,
  confirmError,
  userGeoLocation,
  operatorBranches,
  onConfirm,
}) => {
  useInterfaceLanguage();
  // Маппинг по типу оператора
  const typeConfig = {
    topbase: {
      baseLabel: tr("База"),
      baseName: scannedDriver?.top_base_name || '',
      noLoadMsg: tr("Нет погрузки для вашей Базы"),
      outOfRangeMsg: tr("Необходимо находиться в радиусе 200 метров от одного из филиалов базы"),
    },
    chpp: {
      baseLabel: tr("ТЭЦ"),
      baseName: scannedDriver?.chpp_name || '',
      noLoadMsg: tr("Нет погрузки для вашей ТЭЦ"),
      outOfRangeMsg: tr("Необходимо находиться в радиусе 200 метров от одного из филиалов ТЭЦ"),
    },
    privatepoint: {
      baseLabel: tr("Частная точка"),
      baseName: scannedDriver?.private_point_name || '',
      noLoadMsg: tr("Нет погрузки для вашей соц.объекта или МСУ"),
      outOfRangeMsg: tr("Необходимо находиться в радиусе 200 метров от одного из филиалов соц.объекта или МСУ"),
    },
  };
  const config = typeConfig[operatorType] || typeConfig.topbase;

  const showStatus = operatorType !== 'topbase';
  const isInactive = scannedDriver?.status === 'Не активен';

  return (
    <>
      {tr(scanLoading && <div>{tr("Загрузка данных водителя...")}</div>)}
      {tr(scanError && <div style={{ color: "var(--theme-danger-text, red)" }}>{tr(scanError)}</div>)}
      {tr(scannedDriver && (
        <div className={styles.driverdata}>
          <div><b>{tr("ФИО:")}</b> {tr(scannedDriver.employee_name)}</div>
          <div><b>{tr("Телефон:")}</b> {tr(scannedDriver.employee_phone)}</div>
          <div><b>{tr("Доп. телефон:")}</b> {tr(scannedDriver.phone)}</div>
          <div><b>{tr("Авто номер:")}</b> <Vehicle number={scannedDriver.vehicle_number} model={scannedDriver.vehicle_model} /></div>
          <div><b>{tr("Авто модель:")}</b> {tr(scannedDriver.vehicle_model)}</div>
          <div><b>{tr(config.baseLabel)}:</b> {tr(config.baseName)}</div>
          {tr(showStatus && <div><b>{tr("Статус:")}</b> {tr(scannedDriver.status || tr("Свободен"))}</div>)}

          {tr(showStatus && isInactive && (
            <div style={{ color: "var(--theme-warning-text, orange)", marginTop: 8, fontWeight: 'bold', fontSize: 16 }}>{tr("Водитель не активен, обратитесь в администрацию")}</div>
          ))}

          {lastLoadA ? (
            <div style={{ marginTop: 8, background: "var(--theme-surface-muted, #f5f5f5)", padding: 8, borderRadius: 6 }}>
              <div><b>{tr("Последняя загрузка (Кара-Кече):")}</b></div>
              <div><b>{tr("Статус:")}</b> {tr(lastLoadA.status)}</div>
              <div><b>{tr("Время въезда:")}</b> <DateTime value={lastLoadA.time_a} /></div>
              <div><b>{tr("Отметил (Кара-Кече):")}</b> {tr(lastLoadA.guard_name || '')}</div>
            </div>
          ) : (
            <div style={{ color: "var(--theme-danger-text, red)", marginTop: 8 }}>{tr(config.noLoadMsg)}</div>
          )}

          {/* Проверка геозоны и кнопка подтверждения */}
          {tr(lastLoadA && (!showStatus || !isInactive) && (() => {
            const { isInRange, distanceInfo } = checkBranchesInRange(userGeoLocation, operatorBranches);

            // Геолокация недоступна
            if (!userGeoLocation || typeof userGeoLocation !== 'string') {
              return null; // кнопка всё равно появится через auto-submit effect
            }

            if (!isInRange) {
              return (
                <div className={styles.outOfRangeWarning}>
                  <div className={styles.warningIcon}><TriangleAlert size={22} /></div>
                  <div>
                    <div className={styles.warningTitle}>{tr("Оператор вне зоны действия отметки")}</div>
                    <div className={styles.warningText}>{tr(distanceInfo)}</div>
                    <div className={styles.warningSubtext}>{tr(config.outOfRangeMsg)}</div>
                  </div>
                </div>
              );
            }

            return (
              <>
                {tr(distanceInfo && (
                  <div style={{ color: "var(--theme-success-text, green)", marginBottom: 8, fontWeight: 'bold' }}>{tr("Ближайший филиал: ")}{tr(distanceInfo)}
                  </div>
                ))}
                <button onClick={onConfirm} disabled={confirmLoading} className={styles.btn_tabel_driver}>
                  {confirmLoading ? tr("Отправка...") : tr("Подтвердить получение")}
                </button>
              </>
            );
          })())}
          {tr(confirmError && <div style={{ color: "var(--theme-danger-text, red)" }}>{tr(confirmError)}</div>)}
        </div>
      ))}
    </>
  );
};

export default React.memo(OperatorDriverPanel);
